import {
	getColorScale,
	getPercentageFromContinentsObj,
	getPercentageFromCountriesVals,
	getPOI,
	getPOIProjectedCoords,
	getPOIs,
	getProjection,
	getValuesFromCountriesByContintents,
} from './helpers';

import { scaleLinear } from 'd3-scale';
import {
	max,
	easeLinear,
	interpolate,
	selection,
	geoPath,
	min,
	select,
	zoom,
} from 'd3';
import {
	CONTINENTS_NAMES,
	FILTER_KEYWORDS,
	MIN_SCALE_VALUE,
	MIN_VALUE,
	WORLD_CONTINENTS,
	WORLD_COUNTRIES,
} from './constants';
import { useRef, useEffect, useState } from '@wordpress/element';
import * as topojson from 'topojson';
import cartogram from './libs/cartogram';

import { convertRemToPixels, getName, useWindowSize } from '../helpers';
import HiddenCanvas from './hidden-canvas';
import Legends from './legends';
import Tooltip from './tooltip';

const Graph = (props) => {
	const {
		distortUnit,
		data,
		colors,
		distortion: adminDistortion,
		projection: propsProjection,
		strokeColorStrength,
		animationDuration,
		tooltipStyles,
		onSelect,
		strokeWidth,
		gamma,
		iterations,
		areaWeight,
		withLegends,
		legendsNum,
		withGrid,
		gridOpacity = 0.5,
		POISize,
		withZoomButtons = true,
	} = props;
	const [transitionStatus, setTransitionStatus] = useState(0);
	const [tooltipData, setTooltipData] = useState();
	const [selected, setSelected] = useState([]);
	const [zoomTrans, setZoomTrans] = useState({ x: 0, y: 0, k: 1 });

	const [dimensions, setDimensions] = useState({
		width: window.innerWidth,
		height: window.innerWidth / (16 / 7),
	});

	const [windowWidth] = useWindowSize();

	useEffect(() => {
		if (!canvasRef.current) return;
		const { width } = canvasRef.current.parentNode.getBoundingClientRect();

		setDimensions({ width, height: width / (16 / 7) });
		draw();
	}, [windowWidth]);

	// TODO: this smells
	useEffect(() => {
		draw();
	}, [dimensions]);

	const canvasRef = useRef(null);
	const hiddenCanvasRef = useRef(null);

	let percentages;
	let topology;
	let geoKey;

	// Remove Students, Based etc
	let filterData = data.filter((tax) => {
		return !FILTER_KEYWORDS.includes(tax.name);
	});
	// Remove Continents
	if ('country' === distortUnit) {
		filterData = filterData.filter(
			(tax) => !CONTINENTS_NAMES.includes(tax.name)
		);
	}
	const POIs = getPOIs(filterData);
	// Remove splitted POIs
	filterData = filterData.filter((tax) => !POIs.includes(tax));
	const countriesNames = WORLD_COUNTRIES.objects.countries.geometries.map(
		(g) => g.properties.NAME
	);

	if ('country' === distortUnit) {
		filterData = filterData.filter((t) => countriesNames.includes(t.name));
		percentages = getPercentageFromCountriesVals(filterData);

		topology = WORLD_COUNTRIES;
		geoKey = 'countries';
	} else {
		const continentsValues =
			getValuesFromCountriesByContintents(filterData);
		percentages = getPercentageFromContinentsObj(continentsValues);
		topology = WORLD_CONTINENTS;
		geoKey = 'continent';
	}
	// Remove geometries without values
	const geometries = topology.objects[geoKey].geometries.filter((f) =>
		Object.keys(percentages).includes(f.properties.NAME)
	);

	const values = Object.values(percentages);

	const maxVal = max(values);

	const colorScale = getColorScale(colors, maxVal, gamma);

	// invert value so the bigger the distorsion number the bigger the distortion(1000) and soften values
	const distortion = transitionStatus;

	const featuresGrid = topojson.feature(
		topology,
		topology.objects[geoKey]
	).features;
	const featCollection = {
		type: 'FeatureCollection',
		features: featuresGrid,
	};
	const projection = getProjection(propsProjection).fitSize(
		[dimensions.width, dimensions.height],
		featCollection
	);
	const pathGrid = geoPath().projection(projection);

	const areas = featuresGrid.reduce(
		(acc, f) => ({ ...acc, [f.properties.NAME]: pathGrid.area(f) }),
		{}
	);
	const areasVals = Object.values(areas);
	const maxArea = max(areasVals);
	const minArea = min(areasVals);
	const getAreaPerc = (x) => {
		return (
			((x - minArea) / (maxArea - minArea)).toFixed(2) *
			(areaWeight / 100)
		);
	};
	const maxValWithArea = maxVal + getAreaPerc(maxArea);

	const getValue = (name, toShow) => {
		if (toShow) {
			return percentages[name] || MIN_VALUE;
		}
		const area = areas[name] || 0;
		const result = getAreaPerc(area) + percentages[name] || MIN_VALUE;

		return result;
	};

	const scale = scaleLinear()
		.domain([MIN_VALUE, maxValWithArea])
		.range([MIN_SCALE_VALUE, distortion]);

	const carto = cartogram(iterations)
		.projection(projection)
		.properties((f) => {
			return {
				...f.properties,
				...data.find(
					(d) =>
						d.name === f.properties.NAME ||
						d.name === f.properties.CONTINENT
				),
			};
		})
		.value((d) => {
			return scale(getValue(d.properties.NAME));
		});

	let features = carto(topology, geometries).features;
	if (!adminDistortion || !iterations || !animationDuration) {
		features = featuresGrid;
		carto.path = pathGrid;
	}

	// Now we can work on setting the zoom translate extent:
	// Zooomed projected limits of screen in lat long:
	const invertZeroPoint = projection.invert([0, 0]);
	const left = invertZeroPoint[0];
	const top = invertZeroPoint[1];

	const invertFullPoint = projection.invert([
		dimensions.width,
		dimensions.height,
	]);
	const right = invertFullPoint[0];
	const bottom = invertFullPoint[1];

	// Projected limits of screen in projected unzoomed coordinates:
	const topLeft = projection([left, top]);
	const bottomRight = projection([right, bottom]);

	// Set the zoom translate extent:
	const zoomObj = zoom()
		.translateExtent([topLeft, bottomRight])
		.scaleExtent([1, 15])
		.on('zoom', (e) => {
			setZoomTrans({
				x: e.transform.x,
				y: e.transform.y,
				k: e.transform.k,
			});
		});

	// Ref: https://reactfordataviz.com/articles/how-to-drive-react-state-with-d3-transitions-for-complex-animation/
	useEffect(() => {
		if (animationDuration && adminDistortion) {
			// Creates transition as in d3
			selection()
				.transition('map-transform')
				.duration(animationDuration * 1000)
				.ease(easeLinear)
				.tween('transitionStatus', () => {
					const percentInterpolate = interpolate(
						1,
						adminDistortion * 10
					);

					return (t) => setTransitionStatus(percentInterpolate(t));
				});
		}
	}, [adminDistortion, animationDuration, iterations]);

	function draw() {
		if (!canvasRef.current) return;
		const context = canvasRef.current.getContext('2d');
		context.save();

		context.clearRect(0, 0, dimensions.width, dimensions.height);

		carto.path.context(context);
		pathGrid.context(context);

		const { x, y, k } = zoomTrans;
		context.translate(x, y);
		context.scale(k, k);

		if (withGrid) {
			// Draw World Grid
			featuresGrid.forEach((f) => {
				context.beginPath();
				// Draw land
				pathGrid(f);

				context.strokeStyle = `rgba(111, 111, 111, ${gridOpacity})`;
				context.stroke();
			});
		}

		// Draw Cartogram
		features.forEach((f) => {
			if (
				colors[1]?.length === '#5e785600'.length &&
				colors[1]?.slice('#5e7856'.length, '#5e7856'.length + 2) ===
					'00'
			) {
				// Color is transparent
				return;
			}

			const val = getValue(f.properties.NAME, true);
			let fill;

			if (val) fill = colorScale(val);
			else fill = 'transparent';

			const isSelected = selected.find((s) => {
				return getName(s) === getName(f);
			});

			let lineWidth = isSelected ? strokeWidth * 2 : strokeWidth;
			let stroke;
			if (isSelected && val) stroke = colorScale(maxVal);
			if (!isSelected && val)
				stroke = colorScale(val * strokeColorStrength);
			if (!val) {
				stroke = 'transparent';
				lineWidth = 0;
			}

			context.beginPath();
			// Draw land
			carto.path(f);

			context.lineWidth = lineWidth;
			context.fillStyle = fill;
			context.strokeStyle = stroke;

			context.fill();
			context.stroke();
		});

		const poiRectSize = (POISize * k) / k;
		POIs.forEach((poi) => {
			const POI = getPOI(poi, filterData);

			if (!(POI && POI.coordinates)) {
				return;
			}
			const [coorX, coorY] = getPOIProjectedCoords(POI, projection);

			context.fillStyle = colors[2];
			const isSelected = selected.find((s) => {
				return getName(s) === getName(poi);
			});
			let rectSize = poiRectSize;
			if (isSelected) {
				rectSize = isSelected ? poiRectSize * 2 : poiRectSize;
			}

			context.fillRect(coorX, coorY, rectSize, rectSize);
		});

		if (POIs.length > 2) {
			let fontSize = 0.5;
			let textPosExtra = 0;
			let extraPosRect = 0;

			if (windowWidth > 500) {
				fontSize = 1;
				textPosExtra = 10;
				extraPosRect = 2.5;
			}
			context.font = `${fontSize}rem IBM Plex Sans`;
			const copy = 'Based cities and/or occupations';

			context.fillStyle = colors[2];

			context.fillRect(
				dimensions.width -
					(poiRectSize * 2 +
						convertRemToPixels(fontSize) * copy.length),
				dimensions.height - extraPosRect - textPosExtra - poiRectSize,
				poiRectSize,
				poiRectSize
			);

			context.fillStyle = 'black';
			context.fillText(
				copy,
				dimensions.width - convertRemToPixels(fontSize) * copy.length,
				dimensions.height - textPosExtra
			);
		}
		context.restore();
	}
	draw();

	return (
		<>
			<HiddenCanvas
				dimensions={dimensions}
				path={carto.path}
				projection={projection}
				features={features.filter(
					(f) => percentages[f.properties.NAME]
				)}
				onSelect={onSelect}
				selected={selected}
				setSelected={setSelected}
				setTooltipData={setTooltipData}
				zoomTrans={zoomTrans}
				setZoomTrans={setZoomTrans}
				POIs={POIs}
				data={data}
				POISize={POISize}
				style={{
					pointerEvents: 'none',
				}}
				zoom={zoomObj}
				ref={hiddenCanvasRef}
			/>
			<canvas
				className="map-canvas"
				ref={canvasRef}
				id="map"
				preserveAspectRatio="xMidYMid meet"
				width={dimensions.width}
				height={dimensions.height}
			/>

			{tooltipData && (
				<Tooltip
					getValue={getValue}
					tooltipData={tooltipData}
					tooltipStyles={tooltipStyles}
				/>
			)}
			{withLegends && (
				<Legends
					entries={Object.entries(percentages)}
					colorScale={colorScale}
					legendsNum={legendsNum}
				/>
			)}
			{withZoomButtons && (
				<ZoomButtons
					zoomObj={zoomObj}
					canvas={hiddenCanvasRef.current}
				/>
			)}
		</>
	);
};

const ZoomButtons = ({ zoomObj, canvas }) => {
	const [zoomDirection, setZoomDirection] = useState();
	const [topPos, setTopPos] = useState();

	useEffect(() => {
		if (zoomDirection) {
			const newStatus = zoomDirection === 'in' ? 1.5 : 0.5;

			zoomObj.scaleBy(
				select(canvas)
					.transition(easeLinear)
					.duration(1000)
					.on('end', function () {
						setZoomDirection();
					}),
				newStatus
			);
		}
	}, [zoomDirection]);

	useEffect(() => {
		setTimeout(() => {
			setTopPos(canvas && canvas.height - 50);
		}, 10);
	}, [canvas]);

	return (
		<div
			className="zoom-buttons"
			style={{
				top: topPos,
			}}
		>
			<button id="zoom-in" onClick={() => setZoomDirection('in')}>
				+
			</button>
			<button id="zoom-out" onClick={() => setZoomDirection('out')}>
				-
			</button>
		</div>
	);
};

export default Graph;
